import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'trial' | 'month' | 'year';
  description: string;
  features: string[];
  popular?: boolean;
  trialDays?: number;
}

interface SubscriptionPlansProps {
  onSelectPlan: (plan: SubscriptionPlan) => void;
  currentPlanId?: string;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ onSelectPlan, currentPlanId }) => {
  const plans: SubscriptionPlan[] = [
    {
      id: 'price_1S82AyBX5zvFrqexYopDb9Ro', 
      name: 'Free Trial',
      price: 0,
      interval: 'trial',
      description: 'Try our service for free',
      features: [
        'Full access to all features',
        'Up to 3 emergency contacts',
        'Email support',
        'Automatic alerts'
      ],
      trialDays: 14
    },
    {
      id: 'price_1S8KdEBX5zvFrqex8uY4K4gG', 
      name: 'Monthly Plan',
      price: 9.99,
      interval: 'month',
      description: 'Billed monthly',
      features: [
        'Full access to all features',
        'Up to 5 emergency contacts',
        'Priority email support',
        'Automatic alerts',
        'Monthly usage reports'
      ],
      popular: true
    },
    {
      id: 'price_1S8KdtBX5zvFrqex9pCsVJvj', 
      name: 'Annual Plan',
      price: 99.99,
      interval: 'year',
      description: 'Billed annually (Save 17%)',
      features: [
        'Full access to all features',
        'Up to 10 emergency contacts',
        'Priority email & phone support',
        'Automatic alerts',
        'Monthly usage reports',
        'Advanced analytics'
      ]
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Choose Your Plan
      </Typography>
      <Typography variant="body1" paragraph>
        Select the plan that best fits your needs. All plans include our core protection features.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mt: 2 }}>
        {plans.map((plan) => (
          <Box key={plan.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative',
                border: plan.popular ? '2px solid' : '1px solid',
                borderColor: plan.popular ? 'primary.main' : 'divider'
              }}
            >
              {plan.popular && (
                <Chip
                  icon={<StarIcon />}
                  label="Most Popular"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    right: 20,
                    fontWeight: 'bold'
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h3" gutterBottom>
                  {plan.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="h4" component="span">
                    ${plan.price}
                  </Typography>
                  <Typography variant="subtitle1" component="span" sx={{ ml: 1 }}>
                    {plan.interval === 'trial' 
                      ? `/ ${plan.trialDays} days` 
                      : plan.interval === 'month' 
                        ? '/ month' 
                        : '/ year'}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {plan.description}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button 
                  variant={currentPlanId === plan.id ? "outlined" : "contained"}
                  color="primary"
                  fullWidth
                  onClick={() => onSelectPlan(plan)}
                  disabled={currentPlanId === plan.id}
                >
                  {currentPlanId === plan.id 
                    ? 'Current Plan' 
                    : plan.interval === 'trial' 
                      ? 'Start Free Trial' 
                      : 'Select Plan'}
                </Button>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SubscriptionPlans;
